import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { BASE_URI, prettyPrintDate } from '../Config';
import DownloadButton from './DownloadButton';
import { MhcAttentionJob } from './types';

interface JobsProps {
    userId: string;
}

const PAGE_SIZE = 10;

function formatModelType(modelType: string): string {
    return modelType === 'hlae' ? 'MhcAttention HLA-E' : 'MhcAttention';
}

function getPageFromSearch(search: string): number {
    const params = new URLSearchParams(search);
    const page = Number(params.get('page'));
    if (!Number.isInteger(page) || page < 1) {
        return 0;
    }
    return page - 1;
}

const JobList: React.FC<JobsProps> = ({ userId }) => {
    const [jobs, setJobs] = useState<MhcAttentionJob[]>([]);
    const location = useLocation();
    const navigate = useNavigate();
    const { userToken } = useAuth();

    const [jobsPage, setJobsPage] = useState<number>(() => getPageFromSearch(location.search));

    useEffect(() => {
        setJobsPage(getPageFromSearch(location.search));
    }, [location.search]);

    useEffect(() => {
        const nextPage = jobsPage + 1;
        const params = new URLSearchParams(location.search);
        if (params.get('page') !== String(nextPage)) {
            navigate(`${location.pathname}?page=${nextPage}`, { replace: true });
        }
    }, [jobsPage, location.pathname, location.search, navigate]);

    useEffect(() => {
        let ignore = false;

        const fetchJobs = async () => {
            try {
                const response = await fetch(`${BASE_URI}/mhcattention/jobs/list/${userId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${userToken}`
                    }
                });

                const data = await response.json();

                if (!ignore && response.ok && Array.isArray(data.jobs)) {
                    setJobs(data.jobs);
                }
            } catch (error) {
                console.error('There was an error retrieving MHC Attention jobs:', error);
            }
        };

        fetchJobs();
        const intervalId = window.setInterval(fetchJobs, 10000);

        return () => {
            ignore = true;
            window.clearInterval(intervalId);
        };
    }, [userId, userToken]);

    useEffect(() => {
        const totalPages = Math.max(1, Math.ceil(jobs.length / PAGE_SIZE));
        if (jobsPage > totalPages - 1) {
            setJobsPage(totalPages - 1);
        }
    }, [jobs, jobsPage]);

    const sortedJobs = [...jobs].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    const totalPages = Math.max(1, Math.ceil(sortedJobs.length / PAGE_SIZE));
    const jobsPageSubset = sortedJobs.slice(jobsPage * PAGE_SIZE, (jobsPage + 1) * PAGE_SIZE);

    if (jobs.length === 0) {
        return (
            <div className="empty_state">
                <h3>Submitted Jobs</h3>
                <p>No MHC Attention jobs have been submitted yet.</p>
            </div>
        );
    }

    return (
        <div>
            <h3 className="jobs">Submitted Jobs</h3>
            <div className="pagination jobs">
                <span className="current_page">Page {jobsPage + 1} of {totalPages}</span>
                {jobsPage > 0 && (
                    <span className="previous_page" onClick={() => setJobsPage(jobsPage - 1)}>
                        Previous
                    </span>
                )}
                {jobsPage + 1 < totalPages && (
                    <span className="next_page" onClick={() => setJobsPage(jobsPage + 1)}>
                        Next
                    </span>
                )}
            </div>
            <div className="table_shell">
                <table>
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Input file</th>
                            <th>Model</th>
                            <th>Status</th>
                            <th>Results</th>
                            <th>Download</th>
                        </tr>
                    </thead>
                    <tbody>
                        {jobsPageSubset.map((job) => (
                            <tr key={job.id}>
                                <td>{prettyPrintDate(job.created_at)}</td>
                                <td>
                                    <DownloadButton
                                        text={job.source_file_name}
                                        filename={`input_${job.source_file_name}`}
                                        url_str={`mhcattention/jobs/${job.id}/download/input`}
                                    />
                                </td>
                                <td>{formatModelType(job.model_type)}</td>
                                <td>{job.status}</td>
                                <td>
                                    <Link to={`/jobs/${job.id}`}>Link</Link>
                                </td>
                                <td>
                                    {job.status === 'Succeeded' ? (
                                        <DownloadButton
                                            text="Link"
                                            filename={`output_${job.source_file_name}`}
                                            url_str={`mhcattention/jobs/${job.id}/download/output`}
                                        />
                                    ) : null}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default JobList;
